
import { createSearchWorker } from './searchWorker';

// ─── DOM ENGINE ─────────────────────────────────────────────────────────────
export class DomSearchEngine {
  constructor() {
    this._type = 'dom';
    this._index = [];
    this._idx = -1;
    this._activeMark = null;
    this._matches = [];
  }

  buildIndex(containerEl) {
    this._index = [];
    if (!containerEl) return;

    const walker = document.createTreeWalker(
      containerEl,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          const tag = node.parentNode?.nodeName;
          if (tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
          if (node.parentNode?.closest?.('.doc-search-nav')) return NodeFilter.FILTER_REJECT;
          return node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
        }
      }
    );

    let node;
    while ((node = walker.nextNode())) {
      this._index.push({ node, lowerText: node.textContent.toLowerCase() });
    }
  }

  search(query) {
    this._clearMark();
    this._matches = [];
    this._idx = -1;

    if (!query || query.length < 2) return 0;

    const q = query.toLowerCase();
    const re = new RegExp(query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');

    this._index.forEach(entry => {
      if (!entry.lowerText.includes(q)) return;
      let m;
      re.lastIndex = 0;
      while ((m = re.exec(entry.node.textContent)) !== null) {
        this._matches.push({
          node: entry.node,
          start: m.index,
          end: m.index + m[0].length
        });
      }
    });

    if (this._matches.length > 0) {
      this._idx = 0;
      this._applyMark(this._matches[0]);
    }
    return this._matches.length;
  }

  navigate(dir) {
    const len = this._matches?.length ?? 0;
    if (!len) return { current: 0, total: 0 };

    this._clearMark();
    this._idx = ((this._idx + dir) + len) % len;
    this._applyMark(this._matches[this._idx]);

    return { current: this._idx + 1, total: len };
  }

  clear() {
    this._clearMark();
    this._matches = [];
    this._idx = -1;
  }

  _applyMark(entry) {
    if (!entry) return;
    try {
      const range = document.createRange();
      range.setStart(entry.node, entry.start);
      range.setEnd(entry.node, entry.end);

      const mark = document.createElement('mark');
      mark.className = 'doc-active-match';
      range.surroundContents(mark);

      this._activeMark = mark;
      mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {
      entry.node.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  _clearMark() {
    if (!this._activeMark) return;
    const mark = this._activeMark;
    const parent = mark.parentNode;
    if (parent) {
      parent.replaceChild(document.createTextNode(mark.textContent), mark);
      parent.normalize();
    }
    this._activeMark = null;
  }
}

// ─── IFRAME ENGINE (Fixed + Improved) ───────────────────────────────────────
export class IframeSearchEngine {
  constructor() {
    this._type = 'iframe';
    this._iframeRef = null;
    this._ready = false;
    this._pendingQuery = null;
    this._eid = 'search_' + Math.random().toString(36).substr(2, 9);
    this._worker = null;
    this._nodeMap = new Map();
    this._matches = [];
    this._idx = -1;
    this._activeMark = null;
    this._activeNodeId = null;
    this._currentQuery = '';
    this._onResults = null;
    this._initialScroll = { top: 0, left: 0 };
  }

  setRef(iframeRef) {
    this._iframeRef = iframeRef;
  }

  onLoad() {
    const doc = this._getDoc();
    if (!doc) return;

    this._destroyWorker();
    this._nodeMap.clear();
    this._matches = [];
    this._idx = -1;
    this._activeMark = null;
    this._activeNodeId = null;
    this._ready = false;

    // Capture the scroll position to return to when the search is cleared
    const win = doc.defaultView;
    this._initialScroll = {
      top: win?.scrollY ?? 0,
      left: win?.scrollX ?? 0,
    };

    this._worker = createSearchWorker();

    this._worker.onmessage = (e) => {
      const { type, results } = e.data;
      if (type === 'INDEX_READY') {
        this._ready = true;
        if (this._pendingQuery) {
          const q = this._pendingQuery;
          this._pendingQuery = null;
          this.search(q);
        }
      }
      if (type === 'SEARCH_RESULTS') {
        this._matches = results || [];
        this._idx = this._matches.length > 0 ? 0 : -1;
        this._clearMark();
        if (this._matches.length > 0) {
          this._applyMark(this._matches[0]);
        }
        this._onResults?.(this._matches.length);
      }
    };

    // Build index from parent context
    const nodes = [];
    let id = 0;
    const walker = doc.createTreeWalker(
      doc.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          const tag = node.parentNode?.nodeName;
          if (tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
          return node.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
        }
      }
    );

    let node;
    while ((node = walker.nextNode())) {
      const nodeId = `n_${this._eid}_${id++}`;
      this._nodeMap.set(nodeId, node);
      nodes.push({ nodeId, text: node.textContent });
    }

    this._worker.postMessage({ type: 'BUILD_INDEX', payload: { nodes } });
  }

  search(query) {
    this._currentQuery = query || '';

    this._clearMark(); // Always safe now

    if (!this._ready) {
      this._pendingQuery = (query && query.length >= 2) ? query : null;
      return;
    }

    this._matches = [];
    this._idx = -1;

    if (!query || query.length < 2) {
      this._onResults?.(0);
      return;
    }

    this._worker?.postMessage({ type: 'SEARCH', payload: { query } });
  }

  navigate(dir) {
    const len = this._matches.length;
    if (!len) return { current: 0, total: 0 };

    this._clearMark();
    this._idx = ((this._idx + dir) + len) % len;
    this._applyMark(this._matches[this._idx]);

    return { current: this._idx + 1, total: len };
  }

  clear() {
    this._clearMark();
    this._matches = [];
    this._idx = -1;
    this._currentQuery = '';
    this._onResults?.(0);
    this._resetScroll();
  }

  destroy() {
    this.clear();
    this._destroyWorker();
    this._nodeMap.clear();
    this._ready = false;
    this._pendingQuery = null;
  }

  _destroyWorker() {
    if (this._worker) {
      this._worker.terminate();
      if (this._worker._blobUrl) URL.revokeObjectURL(this._worker._blobUrl);
      this._worker = null;
    }
  }

  _getDoc() {
    const iframe = this._iframeRef?.current;
    return iframe?.contentDocument || iframe?.contentWindow?.document || null;
  }

  _applyMark(entry) {
    if (!entry) return;
    const doc = this._getDoc();
    if (!doc) return;

    const textNode = this._nodeMap.get(entry.nodeId);
    if (!textNode || !textNode.parentNode) return;

    this._clearMark(); // extra safety

    try {
      const range = doc.createRange();
      range.setStart(textNode, entry.start);
      range.setEnd(textNode, entry.end);

      const mark = doc.createElement('mark');
      mark.id = `__search_mark_${this._eid}__`;
      mark.style.cssText = 'background:#FF6B00;color:#000;border-radius:2px;padding:0 1px;';

      range.surroundContents(mark);
      this._activeMark = mark;
      this._activeNodeId = entry.nodeId;

      // mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this._scrollToMarkInIframe(doc, mark); // ⬅ replaced scrollIntoView
    } catch (err) {
      console.warn('Mark apply failed:', err);
      textNode.parentElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  // Scrolls ONLY the iframe's own document, never touches parent page
  _scrollToMarkInIframe(doc, el) {
    if (!doc || !el) return;
    const scroller = doc.scrollingElement || doc.documentElement || doc.body;
    if (!scroller) return;

    const elRect = el.getBoundingClientRect();
    const win = doc.defaultView;
    const viewportHeight = win?.innerHeight || scroller.clientHeight;

    const offset = elRect.top + (win?.scrollY || scroller.scrollTop) - (viewportHeight / 2);

    win?.scrollTo({ top: offset, behavior: 'smooth' });
  }

  // Restores the iframe to the scroll position it had when the doc first loaded
  _resetScroll() {
    const doc = this._getDoc();
    const win = doc?.defaultView;
    if (!win) return;
    win.scrollTo({
      top: this._initialScroll.top,
      left: this._initialScroll.left,
      behavior: 'smooth',
    });
  }

  _clearMark() {
    const doc = this._getDoc();
    if (!doc) return;

    const mark = doc.getElementById(`__search_mark_${this._eid}__`);
    if (!mark?.parentNode) {
      this._activeMark = null;
      this._activeNodeId = null;
      return;
    }

    const parent = mark.parentNode;
    const prevSibling = mark.previousSibling;
    const replacement = doc.createTextNode(mark.textContent || '');

    try {
      parent.replaceChild(replacement, mark);

      // Manually re-merge instead of relying on normalize() (which DELETES
      // zero-length text nodes instead of just merging them, and could drop
      // the exact node our _nodeMap points to — that was the root cause of
      // "highlight sometimes fails").
      let survivor = replacement;
      if (prevSibling && prevSibling.nodeType === Node.TEXT_NODE) {
        prevSibling.nodeValue += survivor.nodeValue;
        survivor.parentNode?.removeChild(survivor);
        survivor = prevSibling;
      }
      const nextSibling = survivor.nextSibling;
      if (nextSibling && nextSibling.nodeType === Node.TEXT_NODE) {
        survivor.nodeValue += nextSibling.nodeValue;
        nextSibling.parentNode?.removeChild(nextSibling);
      }

      // Repoint the map at whichever node actually survived the merge, so
      // future searches hitting this same logical text node keep working.
      if (this._activeNodeId) {
        this._nodeMap.set(this._activeNodeId, survivor);
      }
    } catch (e) {
      console.warn('Failed to clear mark:', e);
    }

    this._activeMark = null;
    this._activeNodeId = null;
  }
}